import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: "app-blog-detail",
    templateUrl: "./blog-detail.component.html",
    styleUrls: ["./blog-detail.component.scss"],
})
export class BlogDetailComponent implements OnInit {
    blog: any;

    blogs = [
        {
            id: 1,
            category: "Solar Guide",
            date: "August 18, 2026",
            title: "How Does a Rooftop Solar System Work?",
            image: 'assets/image/home.png',
            shortDescription:
                "Understand how rooftop solar panels generate electricity and how solar power can be used for homes and businesses.",

            intro: [
                `Rooftop solar systems allow homes and businesses to generate
            electricity from sunlight using solar panels installed on a
            suitable rooftop or structure. They provide a practical way
            to generate renewable electricity closer to where it is used.`,

                `A complete rooftop solar installation includes solar panels,
            an inverter, mounting structures, electrical protection,
            wiring, and other system components. Depending on the system
            configuration, batteries and grid connectivity may also be
            included.`,
            ],

            sections: [
                {
                    title: "What Is a Rooftop Solar System?",

                    paragraphs: [
                        `A rooftop solar system is a solar power installation
                    designed to generate electricity using sunlight. Solar
                    panels are installed on a suitable rooftop or structure
                    where they can receive adequate sunlight throughout the
                    day.`,

                        `The solar panels generate direct current (DC)
                    electricity. A solar inverter converts this DC power
                    into alternating current (AC), which can then be used
                    by electrical appliances and equipment.`,

                        `The size and configuration of the system are generally
                    planned according to electricity consumption, available
                    installation space, solar conditions, and the type of
                    solar solution selected.`,
                    ],
                },

                {
                    title: "How Does Rooftop Solar Work?",

                    paragraphs: [
                        `The working of a rooftop solar system can be understood
                    through a simple flow. Sunlight reaches the solar panels,
                    electricity is generated, the inverter manages the
                    conversion, and the resulting AC electricity is supplied
                    to the property's electrical loads.`,

                        `Depending on the system type, excess solar generation
                    may be stored in batteries or exported to the electricity
                    grid through an applicable and approved arrangement.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "Sunlight Reaches the Solar Panels",

                            description: `Solar panels are positioned to receive available
                            sunlight. Solar cells inside the panels convert
                            the energy from sunlight into DC electricity.`,
                        },

                        {
                            number: "02",

                            title: "Solar Panels Generate Electricity",

                            description: `The panels generate electricity during periods of
                            sunlight. Actual generation can vary depending on
                            system capacity, sunlight availability, shading,
                            orientation, and installation conditions.`,
                        },

                        {
                            number: "03",

                            title: "The Inverter Converts the Power",

                            description: `The solar inverter converts the DC electricity
                            generated by the panels into AC electricity that
                            can be used by standard electrical appliances and
                            equipment.`,
                        },

                        {
                            number: "04",

                            title: "Solar Electricity Powers Your Loads",

                            description: `The generated electricity can be used to power
                            household appliances, office equipment, lighting,
                            pumps, machinery, and other connected electrical
                            loads according to the system design.`,
                        },

                        {
                            number: "05",

                            title: "Excess Energy Is Managed",

                            description: `When solar generation is greater than immediate
                            consumption, the available excess energy can be
                            managed according to the selected system
                            configuration, such as battery storage or an
                            approved grid-export arrangement.`,
                        },
                    ],
                },

                {
                    title: "Main Components of a Rooftop Solar System",

                    paragraphs: [
                        `A rooftop solar installation is made up of several
                    components that work together to generate, convert,
                    distribute, and safely manage electricity.`,

                        `The exact equipment can vary depending on whether the
                    system is on-grid, off-grid, or hybrid and according to
                    the property's energy requirements.`,
                    ],

                    steps: [
                        {
                            number: "01",
                            title: "Solar Panels",
                            description: `Solar panels capture sunlight and convert it
                            into DC electricity.`,
                        },

                        {
                            number: "02",
                            title: "Solar Inverter",
                            description: `The inverter converts DC electricity into AC
                            electricity and manages power flow according
                            to the system configuration.`,
                        },

                        {
                            number: "03",
                            title: "Mounting Structure",
                            description: `The mounting structure securely supports the
                            solar panels and positions them appropriately
                            for solar exposure.`,
                        },

                        {
                            number: "04",
                            title: "Electrical Protection",
                            description: `Cables, connectors, earthing, protection
                            devices, and other accessories help support
                            safe system operation.`,
                        },
                    ],
                },

                {
                    title: "Different Types of Rooftop Solar Systems",

                    paragraphs: [
                        `Rooftop solar systems can be designed in different
                    configurations depending on grid availability, backup
                    requirements, energy usage, and the customer's
                    objectives.`,

                        `An on-grid system is connected to the electricity grid,
                    an off-grid system operates independently with battery
                    storage, while a hybrid system combines solar,
                    battery storage, and grid connectivity.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "On-Grid Solar System",

                            description: `An on-grid solar system remains connected to the
                            electricity grid and can use solar electricity
                            to meet the property's energy requirements.`,
                        },

                        {
                            number: "02",

                            title: "Off-Grid Solar System",

                            description: `An off-grid solar system operates independently
                            from the public electricity grid and generally
                            uses battery storage to make solar energy
                            available when solar generation is insufficient.`,
                        },

                        {
                            number: "03",

                            title: "Hybrid Solar System",

                            description: `A hybrid solar system combines solar generation,
                            battery storage, and grid connectivity to provide
                            a flexible energy management solution.`,
                        },
                    ],
                },

                {
                    title: "What Should You Consider Before Installing Solar?",

                    paragraphs: [
                        `Choosing a rooftop solar system requires more than
                    selecting a panel capacity. The system should be
                    planned around actual electricity consumption,
                    available space, installation conditions, and the
                    desired system type.`,

                        `A proper assessment can help determine the appropriate
                    system capacity and identify the components required
                    for the installation.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "Electricity Consumption",

                            description: `Review current electricity usage and understand
                            the property's typical energy requirements.`,
                        },

                        {
                            number: "02",

                            title: "Available Rooftop Space",

                            description: `Check the available area and whether it is
                            suitable for installing the required number
                            of solar panels.`,
                        },

                        {
                            number: "03",

                            title: "Shading and Site Conditions",

                            description: `Nearby buildings, trees, structures, and other
                            conditions can affect solar generation and
                            should be considered during system planning.`,
                        },

                        {
                            number: "04",

                            title: "System Type",

                            description: `Choose between on-grid, off-grid, or hybrid
                            based on grid availability, backup requirements,
                            and energy objectives.`,
                        },
                    ],
                },
            ],

            benefits: [
                "Generate renewable electricity from sunlight",
                "Make better use of available rooftop space",
                "Reduce dependence on conventional electricity",
                "Select a system according to your energy requirements",
                "Combine solar generation with battery storage where required",
                "Create a more structured approach to long-term energy planning",
            ],

            cta: {
                title: "Find the Right Solar Solution for Your Property",

                buttonText: "Get a Free Consultation",

                link: "/contact",
            },
        },

        {
            id: 2,

            category: "Solar Systems",

            date: "August 15, 2026",

            title: "On-Grid, Off-Grid or Hybrid: Which Solar System Is Right for You?",

            image: "assets/image/about-1.png",

            shortDescription:
                "Compare on-grid, off-grid, and hybrid solar systems to understand which solution may suit your energy requirements.",

            intro: [
                `Solar systems are not one-size-fits-all. The right configuration
            depends on your electricity consumption, grid availability,
            backup requirements, available installation space, and overall
            energy goals.`,

                `The three commonly considered configurations are on-grid,
            off-grid, and hybrid solar systems. Each approach has a
            different way of generating, managing, storing, and supplying
            electricity.`,
            ],

            sections: [
                {
                    title: "On-Grid Solar System",

                    paragraphs: [
                        `An on-grid solar system is connected to the public
                    electricity grid. Solar electricity can be used by the
                    property's electrical loads while the grid remains
                    available as an additional source.`,

                        `Where applicable and approved, surplus electricity may
                    be exported to the grid through the relevant net-metering
                    or grid-interconnection arrangement.`,

                        `On-grid solar can be suitable for homes and businesses
                    that already have reliable electricity-grid access and
                    primarily want to generate solar power and reduce their
                    dependence on conventional electricity.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "Solar Generation",

                            description: `Solar panels generate electricity during
                            available sunlight.`,
                        },

                        {
                            number: "02",

                            title: "Power Your Loads",

                            description: `Generated solar electricity can be used by
                            connected appliances and equipment.`,
                        },

                        {
                            number: "03",

                            title: "Grid Connection",

                            description: `The electricity grid remains available as
                            an additional source according to the system
                            configuration.`,
                        },
                    ],
                },

                {
                    title: "Off-Grid Solar System",

                    paragraphs: [
                        `An off-grid solar system operates independently from
                    the public electricity grid. Solar panels generate
                    electricity while battery storage stores energy for
                    use when solar generation is unavailable or insufficient.`,

                        `This type of system can be useful for remote locations,
                    properties without reliable grid access, or applications
                    where greater energy independence is required.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "Generate Solar Power",

                            description: `Solar panels convert available sunlight into
                            electricity.`,
                        },

                        {
                            number: "02",

                            title: "Store Energy",

                            description: `Excess solar generation is stored in batteries
                            for later use.`,
                        },

                        {
                            number: "03",

                            title: "Use Stored Energy",

                            description: `Battery energy can supply connected loads when
                            solar generation is low or unavailable.`,
                        },
                    ],
                },

                {
                    title: "Hybrid Solar System",

                    paragraphs: [
                        `A hybrid solar system combines solar generation,
                    battery storage, and grid connectivity. This allows
                    different energy sources to work together according
                    to the system design and energy requirements.`,

                        `Solar electricity can be used directly, excess energy
                    can be stored in batteries, and grid electricity can
                    provide additional power when required.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "Solar Generation",

                            description: `Solar panels generate electricity that can
                            directly support connected loads.`,
                        },

                        {
                            number: "02",

                            title: "Battery Storage",

                            description: `Available excess solar energy can be stored
                            for later use.`,
                        },

                        {
                            number: "03",

                            title: "Grid Support",

                            description: `Grid electricity can remain available as
                            an additional energy source when required.`,
                        },
                    ],
                },

                {
                    title: "How Do You Choose the Right System?",

                    paragraphs: [
                        `The best solar configuration depends on your specific
                    circumstances rather than simply choosing the system
                    with the most features.`,

                        `Homes and businesses with reliable grid access may
                    consider an on-grid solution. Locations without reliable
                    grid access may benefit from an off-grid configuration,
                    while users looking for solar generation combined with
                    battery storage and grid connectivity may consider
                    a hybrid system.`,
                    ],

                    steps: [
                        {
                            number: "01",

                            title: "Check Grid Availability",

                            description: `Determine whether your property has reliable
                            access to the electricity grid.`,
                        },

                        {
                            number: "02",

                            title: "Understand Your Backup Needs",

                            description: `Consider whether battery storage and backup
                            power are important for your application.`,
                        },

                        {
                            number: "03",

                            title: "Review Your Energy Usage",

                            description: `Understand your daily and monthly electricity
                            consumption before deciding on system capacity.`,
                        },

                        {
                            number: "04",

                            title: "Plan the System Around Your Property",

                            description: `Available rooftop space, site conditions,
                            electrical loads, and future requirements
                            should all be considered.`,
                        },
                    ],
                },
            ],

            benefits: [
                "Understand the differences between on-grid, off-grid, and hybrid systems",
                "Choose a configuration based on your electricity requirements",
                "Consider battery storage where backup is required",
                "Plan solar generation around available rooftop space",
                "Select an energy solution based on grid availability",
            ],

            cta: {
                title: "Not Sure Which Solar System Is Right for You?",

                buttonText: "Talk to Our Experts",

                link: "/contact",
            },
        },

        {
            id: 3,
            category: "Solar Savings",
            date: "August 12, 2026",
            title: "What Should You Consider Before Installing Solar?",
            image: 'assets/image/home2.png',
            shortDescription:
                "Learn about electricity usage, rooftop space, system sizing, installation, and other important factors before going solar.",
            intro: [
                `Installing a solar system is an important energy decision.
            Before selecting equipment or system capacity, it is useful
            to understand how much electricity your property consumes
            and what you expect from the solar installation.`,

                `A well-planned solar project considers the property,
            electricity requirements, available installation space,
            system configuration, and future energy needs.`,
            ],

            sections: [
                {
                    title: "Understand Your Electricity Consumption",

                    paragraphs: [
                        `Your electricity consumption is one of the most
                    important factors when planning a solar system.
                    Reviewing electricity bills and usage patterns can
                    help establish the approximate energy requirement.`,

                        `Understanding when and how electricity is consumed
                    can also help in selecting an appropriate system
                    configuration and capacity.`,
                    ],

                    steps: [
                        {
                            number: "01",
                            title: "Review Your Electricity Bills",
                            description: `Look at recent electricity bills to understand
                            your typical monthly energy consumption.`,
                        },

                        {
                            number: "02",
                            title: "Identify Major Loads",
                            description: `Consider appliances, pumps, air conditioners,
                            machinery, and other equipment that contribute
                            significantly to electricity consumption.`,
                        },

                        {
                            number: "03",
                            title: "Consider Future Requirements",
                            description: `Think about additional appliances, equipment,
                            or changes in electricity usage that may occur
                            in the future.`,
                        },
                    ],
                },

                {
                    title: "Evaluate Your Rooftop or Installation Space",
                    paragraphs: [
                        `Solar panels require suitable space with adequate
                    sunlight exposure. The available area should be assessed
                    before deciding on system capacity.`,

                        `Shading from trees, nearby buildings, water tanks,
                    structures, or other obstacles may affect solar
                    generation and should be considered during planning.`,
                    ],

                    steps: [
                        {
                            number: "01",
                            title: "Available Area",
                            description: `Determine how much suitable space is available
                            for installing the required solar panels.`,
                        },

                        {
                            number: "02",
                            title: "Sunlight Exposure",
                            description: `Review whether the proposed installation area
                            receives adequate sunlight throughout the day.`,
                        },

                        {
                            number: "03",
                            title: "Roof Condition",
                            description: `The roof or supporting structure should be
                            suitable for the proposed solar installation.`,
                        },
                    ],
                },

                {
                    title: "Choose the Right Solar System Type",
                    paragraphs: [
                        `The system configuration should match the property's
                    electricity requirements and grid conditions.`,

                        `An on-grid system can be considered when reliable grid
                    access is available. An off-grid system may be useful
                    where grid electricity is unavailable or unreliable,
                    while a hybrid system combines solar, battery storage,
                    and grid connectivity.`,
                    ],

                    steps: [
                        {
                            number: "01",
                            title: "On-Grid",
                            description: `Suitable for properties connected to the
                            electricity grid that want to generate solar
                            electricity.`,
                        },

                        {
                            number: "02",
                            title: "Off-Grid",
                            description: `Designed for independent operation with
                            battery storage where grid dependence needs
                            to be minimized.`,
                        },

                        {
                            number: "03",
                            title: "Hybrid",
                            description: `Combines solar generation, battery storage,
                            and grid connectivity for a flexible energy
                            solution.`,
                        },
                    ],
                },

                {
                    title: "Consider Installation and Maintenance",
                    paragraphs: [
                        `Solar performance depends not only on the equipment
                    selected but also on proper installation and ongoing
                    system care.`,

                        `Professional site assessment, appropriate system
                    sizing, correct installation, electrical protection,
                    and periodic maintenance can help support reliable
                    system operation.`,
                    ],

                    steps: [
                        {
                            number: "01",
                            title: "Site Assessment",
                            description: `Evaluate the property, available space,
                            electrical requirements, and installation
                            conditions.`,
                        },

                        {
                            number: "02",
                            title: "System Design",
                            description: `Plan the solar capacity and supporting
                            equipment according to the property's
                            requirements.`,
                        },

                        {
                            number: "03",
                            title: "Installation",
                            description: `Install the solar panels, inverter, mounting
                            structure, wiring, and protection equipment
                            correctly.`,
                        },

                        {
                            number: "04",
                            title: "Maintenance",
                            description: `Regular inspection, cleaning, and preventive
                            maintenance can help maintain system
                            performance.`,
                        },
                    ],
                },
            ],

            benefits: [
                "Understand your electricity requirements before installation",
                "Make better use of available rooftop or installation space",
                "Choose between on-grid, off-grid, and hybrid configurations",
                "Plan the system around current and future energy needs",
                "Consider installation, maintenance, and system safety",
                "Make a more informed solar investment decision",
            ],

            cta: {
                title: "Ready to Plan Your Solar Installation?",
                buttonText: "Get a Free Consultation",
                link: "/contact",
            },
        },
    ];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
    ) {}

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get("id"));
        this.blog = this.blogs.find((blog) => Number(blog.id) === id);
    }
}
